import * as Api from '@ViewModels';
import { css, StyleSheet } from 'aphrodite';
import * as React from 'react';
import { getDisplayName } from '../../../../models/NameUtils';
import { useContact } from '../../../../queries/Contact/useContact';
import { useContactAutocomplete } from '../../../../queries/Contact/useContactAutocomplete';
import { Combobox, ComboboxContent, ComboboxEmpty, ComboboxInput, ComboboxList } from '../../../components-v2/Combobox';
import {
	ComboboxAvatarItem,
	ComboboxAvatarItemAvatar,
	ComboboxAvatarItemDescription,
	ComboboxAvatarItemTitle,
} from '../../../components-v2/Combobox/ComboboxAvatarItem';
import { THEME } from '../../../components-v2/Theme/theme';

const RESULT_COUNT = 8;

interface IProps {
	contactId: string | null;
	onChange: (contact: Api.IContact | null) => void;
	impersonationContext?: Api.IImpersonationContext;
}

function initialsOf(name: string): string {
	return name
		.split(/\s+/)
		.filter(Boolean)
		.slice(0, 2)
		.map(part => part[0]?.toUpperCase() ?? '')
		.join('');
}

/** Narrows the list to action items that reference one contact, independent of the text search. */
export const ContactFilter: React.FC<IProps> = ({ contactId, onChange, impersonationContext }) => {
	const [open, setOpen] = React.useState(false);
	const [query, setQuery] = React.useState('');
	const trimmed = query.trim();

	const selectedQuery = useContact({ contactId: contactId ?? '', enabled: !!contactId, allowArchived: true });
	const results = useContactAutocomplete({
		enabled: open && trimmed.length > 0,
		fragment: trimmed,
		pageSize: RESULT_COUNT,
		impersonationContext,
	});
	const items = React.useMemo(() => results.data?.pages[0]?.values ?? [], [results.data]);
	const selected = selectedQuery.data ?? null;
	const selectedLabel = selected ? getDisplayName(selected) : contactId ? 'Loading…' : '';

	return (
		<Combobox
			items={items}
			value={selected}
			onValueChange={value => onChange((value as Api.IContact | null) ?? null)}
			itemToStringLabel={item => getDisplayName(item as Api.IContact)}
			isItemEqualToValue={(item, value) => (item as Api.IContact)?.id === (value as Api.IContact)?.id}
			filter={null}
			open={open}
			onOpenChange={next => {
				setOpen(next);
				setQuery('');
			}}
			inputValue={open ? query : selectedLabel}
			onInputValueChange={setQuery}
		>
			<ComboboxInput
				aria-label='Filter by contact'
				placeholder={open ? 'Search contacts' : 'Any contact'}
				showClear={!!contactId}
				styles={[styleSheet.input, !!contactId && styleSheet.inputActive]}
				onFocus={event => event.currentTarget.select()}
			/>
			<ComboboxContent>
				<ComboboxEmpty>{trimmed ? 'No contacts match' : 'Type a name to filter by contact'}</ComboboxEmpty>
				<ComboboxList>
					{(contact: Api.IContact) => (
						<ComboboxAvatarItem key={contact.id} value={contact}>
							<ComboboxAvatarItemAvatar
								imageUrl={contact.profilePic || undefined}
								fallbackText={initialsOf(getDisplayName(contact))}
							/>
							<div className={css(styleSheet.itemText)}>
								<ComboboxAvatarItemTitle>{getDisplayName(contact)}</ComboboxAvatarItemTitle>
								<ComboboxAvatarItemDescription>{contact.primaryEmail?.value}</ComboboxAvatarItemDescription>
							</div>
						</ComboboxAvatarItem>
					)}
				</ComboboxList>
			</ComboboxContent>
		</Combobox>
	);
};

const styleSheet = StyleSheet.create({
	input: {
		width: 220,
	},
	inputActive: {
		borderColor: THEME.colors.primary.main,
	},
	itemText: {
		minWidth: 0,
	},
});
